import { Button, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import moment, { type Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AnimateIn } from '@/components/AnimateIn';
import {
	getEffectiveStatus,
	type RenderableContentArtifact,
} from '@/hooks/useContentArtifacts';
import { isScheduleObject } from '@/spaces/chat/panels/scheduleUtils';
import { cn } from '@/utils/cn';

import type { Action } from '../../../../bridge/types/action.ts';
import type { Process } from '../../../../bridge/types/process.ts';
import {
	FilterToolbar,
	type DimensionConfig,
} from '../campaigns/CampaignLibraryFilters';
import type { ActiveFilter } from '../campaigns/campaignLibraryFilterUtils';

import { ContentAsset } from './ContentAsset';
import { DeleteConfirmationWrapper } from './DeleteConfirmationWrapper';
import { emailSendDate, validMoment } from './emailSendDate';
import { EmptyMessage } from './EmptyMessage';

const TYPE_DIMENSION: DimensionConfig = {
	key: 'type',
	label: 'Type',
	options: [
		{ label: 'Emails', value: 'email' },
		{ label: 'Posts', value: 'instagram_post' },
		{ label: 'Ads', value: 'digital_ad' },
	],
};

const STATUS_DIMENSION: DimensionConfig = {
	key: 'status',
	label: 'Status',
	options: [
		{ label: 'Scheduled', value: 'scheduled' },
		{ label: 'Published', value: 'published' },
		{ label: 'Paused', value: 'paused' },
		{ label: 'Drafts', value: 'draft' },
		{ label: 'Failed', value: 'failed' },
	],
};

const CONTENT_DIMENSIONS: DimensionConfig[] = [
	TYPE_DIMENSION,
	STATUS_DIMENSION,
];

const bulkBarTextStyles = style({
	font: 'body',
	fontSize: 'body-sm',
	color: 'body',
});

interface ContentLibraryProps {
	content: RenderableContentArtifact[];
	processById: Map<string, Process>;
	emailActionByRef: Map<string, Action>;
	filters: ActiveFilter[];
	onFiltersChange: (next: ActiveFilter[]) => void;
	onBulkDelete: (ids: string[]) => Promise<void>;
	onClickContent?: (content: RenderableContentArtifact) => void;
}

const scheduledDateFor = (
	artifact: RenderableContentArtifact,
	action: Action | undefined,
): Moment | null => {
	if (artifact.type === 'instagram_post' || artifact.type === 'digital_ad') {
		return isScheduleObject(artifact.schedule) && 'on' in artifact.schedule
			? validMoment(artifact.schedule.on)
			: null;
	}
	return emailSendDate(artifact, action);
};

const matchesTypeFilter = (
	artifact: RenderableContentArtifact,
	value: string,
): boolean => {
	if (value === 'email') {
		return (
			artifact.type === 'email_html' || artifact.type === 'email_plaintext'
		);
	}
	return artifact.type === value;
};

export const ContentLibrary = function ContentLibrary({
	content,
	processById,
	emailActionByRef,
	filters,
	onFiltersChange,
	onBulkDelete,
	onClickContent,
}: ContentLibraryProps) {
	// Selection is deliberately local: opening a draft unmounts the library, and
	// losing the selection then is the behaviour we want. Filters live in the
	// parent for the opposite reason.
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [confirmOpen, setConfirmOpen] = useState(false);

	const addFilter = useCallback(
		(filter: ActiveFilter) => {
			if (
				filters.some(
					(f) => f.dimension === filter.dimension && f.value === filter.value,
				)
			) {
				return;
			}
			onFiltersChange([...filters, filter]);
		},
		[filters, onFiltersChange],
	);

	const removeFilter = useCallback(
		(filter: ActiveFilter) => {
			onFiltersChange(
				filters.filter(
					(f) =>
						!(f.dimension === filter.dimension && f.value === filter.value),
				),
			);
		},
		[filters, onFiltersChange],
	);

	const clearFilters = useCallback(
		() => onFiltersChange([]),
		[onFiltersChange],
	);

	const filtered = useMemo(() => {
		if (filters.length === 0) return content;
		const typeFilters = filters.filter((f) => f.dimension === 'type');
		const statusFilters = filters.filter((f) => f.dimension === 'status');
		return content.filter((artifact) => {
			if (
				typeFilters.length > 0 &&
				!typeFilters.some((f) => matchesTypeFilter(artifact, f.value))
			) {
				return false;
			}
			if (statusFilters.length > 0) {
				const eff = getEffectiveStatus(artifact, processById);
				if (!statusFilters.some((f) => eff === f.value)) return false;
			}
			return true;
		});
	}, [content, filters, processById]);

	const { sorted, selectableIds } = useMemo(() => {
		const now = moment();
		const upcoming: RenderableContentArtifact[] = [];
		const rest: RenderableContentArtifact[] = [];
		const selectable = new Set<string>();
		for (const artifact of filtered) {
			const eff = getEffectiveStatus(artifact, processById);
			// Only drafts can be bulk deleted. Gating on the effective status keeps
			// artifacts whose parent campaign was cancelled — they still render, but
			// they are already dead — out of the selection.
			if (eff === 'draft') selectable.add(artifact.id);
			const action = emailActionByRef.get(artifact.id);
			const date = scheduledDateFor(artifact, action);
			if (eff === 'scheduled' && date && date.isAfter(now)) {
				upcoming.push(artifact);
			} else {
				rest.push(artifact);
			}
		}
		upcoming.sort((a, b) => {
			const ad = scheduledDateFor(a, emailActionByRef.get(a.id)) ?? moment(0);
			const bd = scheduledDateFor(b, emailActionByRef.get(b.id)) ?? moment(0);
			return ad.diff(bd);
		});
		rest.sort((a, b) => moment(b.updated_at).diff(moment(a.updated_at)));
		return {
			sorted: [...upcoming, ...rest],
			selectableIds: selectable,
		};
	}, [filtered, processById, emailActionByRef]);

	// The grid is rebuilt on every refresh, so a stored id can disappear or stop
	// being a draft while it is still selected. Intersecting here instead of
	// pruning in an effect means a stale id can never reach the delete call.
	const effectiveSelection = useMemo(
		() =>
			sorted.filter((a) => selectedIds.has(a.id) && selectableIds.has(a.id)),
		[sorted, selectedIds, selectableIds],
	);

	// The confirmation is open only while the bulk bar is on screen, but its open
	// flag outlives it. Without this reset an emptied selection leaves the flag
	// set, and the next selection re-mounts the confirmation already open.
	useEffect(() => {
		if (effectiveSelection.length === 0) setConfirmOpen(false);
	}, [effectiveSelection.length]);

	const toggleSelect = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

	const handleConfirmBulkDelete = async () => {
		await onBulkDelete(effectiveSelection.map((a) => a.id));
		setSelectedIds(new Set());
		setConfirmOpen(false);
	};

	const renderEmpty = () => {
		if (filters.length > 0) {
			return (
				<div className="flex flex-col items-center gap-3 py-12">
					<span className="text-sm text-(--text-muted)">
						No content matches your filters
					</span>
					<button
						type="button"
						onClick={clearFilters}
						className="cursor-pointer text-sm text-(--text-secondary) underline
							hover:text-(--text-primary)"
					>
						Clear filters
					</button>
				</div>
			);
		}
		return (
			<EmptyMessage content="Your content library is empty">
				<div className="mt-4 flex flex-col items-start gap-3">
					<span className="text-sm text-(--text-secondary)">
						Get started by generating your first post or email.
					</span>
				</div>
			</EmptyMessage>
		);
	};

	const selectionCount = effectiveSelection.length;
	const draftNoun = selectionCount === 1 ? 'draft' : 'drafts';

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<AnimateIn delay={0}>
				<FilterToolbar
					filters={filters}
					dimensions={CONTENT_DIMENSIONS}
					onAdd={addFilter}
					onRemove={removeFilter}
				/>
			</AnimateIn>

			{selectionCount > 0 && (
				<div
					data-testid="content-bulk-bar"
					className="flex items-center gap-3 rounded-full border
						border-(--glass-border) px-3 py-2"
				>
					<Text styles={bulkBarTextStyles}>
						{selectionCount} {draftNoun} selected
					</Text>
					<Button
						variant="secondary"
						size="S"
						onPress={clearSelection}
						data-testid="content-bulk-clear"
					>
						<Text>Clear</Text>
					</Button>
					<DeleteConfirmationWrapper
						isOpen={confirmOpen}
						onCancel={() => setConfirmOpen(false)}
						onConfirm={handleConfirmBulkDelete}
						title={`Delete ${selectionCount} ${draftNoun}?`}
						body={`${selectionCount === 1 ? 'This draft' : `These ${selectionCount} drafts`} will be removed from your Content Library and will not be posted.`}
					>
						<Button
							variant="negative"
							size="S"
							onPress={() => setConfirmOpen(true)}
							data-testid="content-bulk-delete"
						>
							<Text>Delete</Text>
						</Button>
					</DeleteConfirmationWrapper>
				</div>
			)}

			<div
				data-testid="content-library-scroller"
				className="scrollbar-thin min-h-0 flex-1 overflow-y-auto pb-8"
			>
				<AnimateIn delay={1}>
					{sorted.length === 0 ? (
						renderEmpty()
					) : (
						<div
							data-testid="content-library-grid"
							className={cn(
								'grid gap-x-4 gap-y-8',
								'grid-cols-2 md:grid-cols-3',
							)}
						>
							{sorted.map((artifact) => {
								const action = emailActionByRef.get(artifact.id);
								const effectiveStatus = getEffectiveStatus(
									artifact,
									processById,
								);
								const owningProcess = artifact.process_id
									? processById.get(artifact.process_id)
									: undefined;
								return (
									<ContentAsset
										key={artifact.id}
										artifact={artifact}
										action={action}
										effectiveStatus={effectiveStatus}
										processStatus={owningProcess?.status}
										onClick={() => onClickContent?.(artifact)}
										isSelectable={selectableIds.has(artifact.id)}
										isSelected={selectedIds.has(artifact.id)}
										onToggleSelect={() => toggleSelect(artifact.id)}
									/>
								);
							})}
						</div>
					)}
				</AnimateIn>
			</div>
		</div>
	);
};
